/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import TwoColumns from '../components/layouts/TwoColumns';
import {ColumnContainer, PersonSection} from '../components/layouts/TwoColumns';
import DisconnectedImage from '../assets/images/LandingPage/disconnected.png';
import Image from 'next/image';
export const meta = {
  author: 'Jens Seip'
};
const MDXLayout = ({children}) => _jsx(LandingPageLayout, {
  mdx: true,
  meta: meta,
  title: "How to avoid connectivity issues",
  subTitle: "If participants are unable to connect or stay connected while you’re presenting live, you or they may be experiencing a connectivity issue.",
  image: DisconnectedImage,
  children: children
});
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      blockquote: "blockquote",
      p: "p",
      strong: "strong",
      a: "a",
      h1: "h1",
      h2: "h2",
      h3: "h3"
    }, props.components);
    return _jsxs(_Fragment, {
      children: [_jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Quick check:"
          }), " Want to test your connection right now? ", _jsx(_components.a, {
            href: "/connectivity-issues/diagnose",
            children: "Run the connectivity diagnostic"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h1, {
        children: "What is a connectivity issue?"
      }), "\n", _jsx(_components.p, {
        children: "Anything that causes you or participants to frequently lose connection, or that prevents connection, to our game servers. Most often they are related to network or security settings that you or an IT admin can adjust quickly. But on a rare occasion, there may be server-side issues that require our engineers to fix."
      }), "\n", _jsx(_components.h1, {
        children: "Common connectivity issues"
      }), "\n", _jsx(_components.h2, {
        children: "Participants are unable to connect to the live classroom using PIN"
      }), "\n", _jsx(_components.h3, {
        children: "1. Your school, district or office have a firewall or proxy server or in the web content filtering service"
      }), "\n", _jsx(_components.p, {
        children: "Below are all URLs that must be open in order for a teacher to be able to use Curipod. Share this document with your IT department to permit access."
      }), "\n", _jsx(_components.p, {
        children: "Note: These URLs must be accessible both for the school firewall and the web filter service(GoGuardian, LightSpeed and Securly)."
      }), "\n", _jsxs("table", {
        children: [_jsxs("thead", {
          children: [_jsx("th", {
            children: "URL"
          }), "\n", _jsx("th", {
            children: "What is it used for?"
          })]
        }), _jsxs("tbody", {
          children: [_jsxs("tr", {
            children: [_jsx("td", {
              children: "https://curipod.com"
            }), "\n", _jsx("td", {
              children: "Main page of Curipod, make presentations, host presentations and use the customizable lessons."
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://api.curipod.com"
            }), "\n", _jsx("td", {
              children: "The url to the Curipod server. This domain must be enabled for both teachers and students for Curipod to work."
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://api-live.curipod.com"
            }), "\n", _jsx("td", {
              children: "The url to the Curipod live classroom server. This domain must be enabled for both teachers and students for Curipod to work."
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://curi.live"
            }), "\n", _jsx("td", {
              children: "The student and participant web page"
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://storage.googleapis.com"
            }), "\n", _jsx("td", {
              children: "The url to that students are uploading images to"
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://images-cdn.curipod.com"
            }), "\n", _jsx("td", {
              children: "The url from which lesson images sent to the student devices are downloaded from."
            })]
          }), _jsxs("tr", {
            children: [_jsx("td", {
              children: "https://snapmentorprod.b2clogin.com"
            }), "\n", _jsx("td", {
              children: "The url to Curipod authentication, authorisation and Single Sign On server"
            })]
          })]
        })]
      }), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx(_components.h3, {
        children: "2. Teacher device work, but not student devices"
      }), "\n", _jsx(_components.p, {
        children: "Some schools and organizations are able to set different access levels for specific groups. Your players’ devices may have a more restrictive access level than your device."
      }), "\n", _jsx("br", {}), "\n", _jsx(_components.p, {
        children: "How to solve: Try to connect a student device to a different network, such as a personal hotspot, to see if the issue is resolved. If it is, contact your IT admin to adjust the access level for student devices."
      }), "\n", _jsx("br", {}), "\n", _jsx(_components.p, {
        children: "Still doesn't work? Ask your IT department if Curipod is labelled as \"Generative AI\" in their content filter solution."
      }), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx(_components.h3, {
        children: "3. It is still not working, but I allowed for the URLs in the firewall."
      }), "\n", _jsx(_components.p, {
        children: "It might very well be the web content filter blocking the urls. It's like a double firewall on top of your existing firewall.\nAsk your IT department whether your school and district uses: GoGuardian, Securly or LightSpeed.\nAllowing the urls listed above should make Curipod work perfectly well!"
      }), "\n", _jsx(_components.p, {
        children: "Web content filters might include GEO location filters."
      }), "\n", _jsx(_components.p, {
        children: "Some districts have set up GEO location filters that block access to servers outside of the country. Curipod servers are currently located in the EU and may cause \"US-only\" filters to apply."
      }), "\n", _jsx(_components.p, {
        children: "If you are unable to figure it out - please contact support@curipod.com for further assistance."
      }), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {}), "\n", _jsx("br", {})]
    });
  }
}
export default MDXContent;
